import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/redux/store";
import ATMPagination from "src/components/UI/atoms/ATMPagination/ATMPagination";
import ATMTable, {
  columnTypes,
} from "src/components/UI/atoms/ATMTable/ATMTable";
import ATMTableHeader from "src/components/UI/atoms/ATMTableHeader/ATMTableHeader";
import {
  setRowsPerPage,
  setPage,
  setSearchValue,
  setOrderByValue,
} from "src/redux/slices/GeneratedCertificateSlice";
import FilterGeneratedCertificateCardWrapper from "./filter/FilterGeneratedCertificateCardWrapper";

type Props = {
  columns: columnTypes[];
  rows: any[];
  onExportXlsx: () => void;
  isExporting: boolean;
};

const GeneratedCertificate = ({
  columns,
  rows,
  onExportXlsx,
  isExporting,
}: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const generatedCertificateState: any = useSelector(
    (state: RootState) => state.generatedCertificate
  );

  const {
    page,
    rowsPerPage,
    isTableLoading,
    searchValue,
    totalItems,
    orderByValue,
  } = generatedCertificateState;

  return (
    <div className="border flex flex-col overflow-auto rounded bg-white ">
      {/*Table Header */}
      <ATMTableHeader
        page={page}
        rowCount={totalItems}
        rowsPerPage={rowsPerPage}
        rows={rows}
        onRowsPerPageChange={(newValue) => dispatch(setRowsPerPage(newValue))}
        searchValue={searchValue}
        onSearchChange={(newValue) => dispatch(setSearchValue(newValue))}
        orderValue={orderByValue}
        onOrderByChange={(newValue) => dispatch(setOrderByValue(newValue))}
        isShowOrder={true}
        renderFilter={(close) => (
          <FilterGeneratedCertificateCardWrapper close={close} />
        )}
        isFilter
      />

      {/* Export XLSX Button */}
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <div className="text-sm text-slate-500">
          Only showing records where affidavit certificate PDF has been generated
        </div>
        <button
          onClick={onExportXlsx}
          disabled={isExporting || rows.length === 0}
          className={`px-4 py-2 rounded text-white text-sm font-medium flex items-center gap-2 ${
            isExporting || rows.length === 0
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-green-600 hover:bg-green-700"
          }`}
        >
          {isExporting ? (
            <>
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Exporting...
            </>
          ) : (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Export XLSX
            </>
          )}
        </button>
      </div>

      {/* Table */}
      <div className="border flex flex-col grow overflow-auto rounded bg-white">
        <ATMTable
          columns={columns}
          isLoading={isTableLoading}
          rows={rows}
          rowExtraClasses={(row) => "min-h-[40px]"}
        />
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-end border-t border-slate-300">
        <ATMPagination
          page={page}
          rowCount={totalItems}
          rows={rows}
          rowsPerPage={rowsPerPage}
          onPageChange={(newPage) => dispatch(setPage(newPage))}
        />
      </div>
    </div>
  );
};

export default GeneratedCertificate;
